import React from 'react';

import { useAppStore } from '../../stores';
import { isPreviewRequested } from '../../utils/previewMode';

import SystemMessage from '../SystemMessage/SystemMessage';

import './PreviewModeBanner.scss';

/**
 * Tells staff which reality they are looking at while `?mode=preview` is on.
 *
 * A page quietly showing unpublished content looks exactly like a page that
 * has gone live early — that is a support ticket waiting to happen, so
 * preview always says so. The refusal case matters just as much: preview is
 * authorised server-side against the signed-in user's permission and their
 * access to this community, so a URL can ask for preview and not get it. Left
 * silent, that reads as "my draft place is missing" rather than "I am not
 * signed in as someone who can preview".
 *
 * Renders nothing at all when preview was never asked for, and nothing while
 * the answer is still in flight (previewGranted === null), so a normal
 * session is completely unaffected.
 */
const PreviewModeBanner = () => {
  const settings = useAppStore( ( state ) => state.settings );
  const previewGranted = useAppStore( ( state ) => state.previewGranted );

  if ( !isPreviewRequested( settings ) || previewGranted === null ) {
    return null;
  }

  return (
    <div className="previewModeBanner">
      {previewGranted ? (
        <SystemMessage
          status="warning"
          title="Preview mode"
          message="You are seeing unpublished content as it will appear once published. Visitors do not see this."
          testId="preview-mode:on"
        />
      ) : (
        <SystemMessage
          status="error"
          title="Preview mode unavailable"
          message="You are seeing the published site. Preview needs an account with permission to preview this community — sign in, or ask The Vectre for access."
          testId="preview-mode:denied"
        />
      )}
    </div>
  );
};

export default PreviewModeBanner;
